import { useState } from 'react'
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Sports, School, Person, InfoOutlined } from '@mui/icons-material'
import { useAthleteProfileForm } from '../hooks/useAthleteProfileForm'
import { PersonalInfoFields } from '../components/profile/athlete/PersonalInfoFields'
import { AcademicInfoFields } from '../components/profile/athlete/AcademicInfoFields'
import { AthleticInfoFields } from '../components/profile/athlete/AthleticInfoFields'

const steps = ['Personal Information', 'Academic Information', 'Athletic Data']

/**
 * The 3-step onboarding wizard. Used only by `/onboarding` — once a profile is
 * complete, editing happens on the single-page `/profile/edit` form instead.
 */
export const AthleteProfileWizard = () => {
  const form = useAthleteProfileForm()
  const [activeStep, setActiveStep] = useState(0)

  const handleNext = () => {
    if (form.validateStep(activeStep as 0 | 1 | 2)) {
      setActiveStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    form.setFieldErrors({})
    setActiveStep(prev => prev - 1)
  }

  const handleSave = () => {
    // Onboarding is the only place the wizard lives, so a completed save
    // always publishes the profile.
    void form.save({ isPublic: true })
  }

  if (form.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  const isLastStep = activeStep === steps.length - 1

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <PersonalInfoFields form={form} />
      case 1:
        return <AcademicInfoFields form={form} />
      case 2:
        return <AthleticInfoFields form={form} />
      default:
        return null
    }
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {activeStep === 0 && <Person sx={{ mr: 1, verticalAlign: 'middle' }} />}
        {activeStep === 1 && <School sx={{ mr: 1, verticalAlign: 'middle' }} />}
        {activeStep === 2 && <Sports sx={{ mr: 1, verticalAlign: 'middle' }} />}
        {steps[activeStep]}
      </Typography>

      {form.saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Profile saved successfully!
        </Alert>
      )}
      {form.isSaveHttpError && !form.saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to save profile. Please try again.
        </Alert>
      )}
      {form.saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {form.saveError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {renderStepContent(activeStep)}
          <Box sx={{ mt: 4 }}>
            {isLastStep && (
              <Box
                sx={{
                  mb: 1,
                  px: 1.25,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  ml: 'auto',
                  width: 'fit-content',
                  maxWidth: '100%',
                }}
              >
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}
                >
                  Saving completes onboarding and publishes your profile to recruiters.
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button disabled={activeStep === 0} onClick={handleBack}>
                Back
              </Button>
              {isLastStep ? (
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleSave}
                  disabled={form.isSaving}
                >
                  {form.isSaving ? 'Saving...' : 'Save Profile'}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
